/** @file viewportHud: the HUD snapshot (facing, frame, zoom, the selected joint and its picks) at 10 Hz. */
import { POSE_LAYOUT as L } from '@zilo/pose3d-pose/layout/poseLayout';
import { gizmoAccess } from './gizmoAccess';
import { poseNode } from './poseNode';
import type { ViewportCtx } from './viewportCtx';

/** One facet pick of the selected joint's drawables, as the status bar shows it. */
export type HudPick = { drawable: string; entry: number; tile: number; flip: boolean; row: number; missing: boolean };

/**
 * What the toolbar, orientation cube and status read from the running instance (the rest comes from the store):
 * `readout` the running drag's value as it will be stored, `note` why the tool shows fewer handles or none.
 */
export type ViewportHud = {
  facingDeg: number; frame: number; steps: number | 'free'; scale: number; loading: boolean; readout: string | null; note: string | null;
  selected: { name: string; parent: string | null; yawDeg: number; pitchDeg: number; picks: HudPick[] } | null;
};

/** Milliseconds between two HUD snapshots. */
const PERIOD_MS = 100;

/** The selected (active) joint's parent, pose yaw / pitch and the picks of the drawables on it. */
function selectedOf(ctx: ViewportCtx): ViewportHud['selected'] {
  const p = ctx.puppet();
  const name = ctx.store.getState().selection.nodes.at(-1);
  const index = p === null || name === undefined ? -1 : p.names.indexOf(name);
  if (p === null || name === undefined || index < 0) return null;
  const state = p.instance.state();
  const parent = p.names[poseNode(state.pose, index).parent] ?? null;
  const drawables = new Set(p.source.puppet.drawables.filter((d) => d.node === name).map((d) => d.id));
  const picks = state.picks.filter((k) => drawables.has(k.drawable))
    .map((k) => ({ drawable: k.drawable, entry: k.entry, tile: k.tile, flip: k.flip, row: k.row, missing: k.missing }));
  const o = index * L.stride;
  return { name, parent, yawDeg: state.pose[o + L.yaw] ?? 0, pitchDeg: state.pose[o + L.pitch] ?? 0, picks };
}

/** What the status line says in Quads mode (the stored playhead is kept, the view shows the rest pose). */
const QUADS_NOTE = 'Quads: rest pose (turn Quads off to see the clip)';

/** Quads mode's note, else why the tool shows no handles (the lock) or fewer (the note); null in turntable mode. */
function noteOf(ctx: ViewportCtx): string | null {
  if (ctx.mode !== 'edit') return null;
  const s = ctx.store.getState();
  if (s.camera.quads) return QUADS_NOTE;
  const access = gizmoAccess(s, s.tool);
  return access.lock ?? access.note;
}

/** The HUD as of now. */
function snapshot(ctx: ViewportCtx): ViewportHud {
  const state = ctx.puppet()?.instance.state();
  const base = {
    scale: ctx.camera?.fit()?.scale ?? 1, loading: ctx.store.getState().preview.loading, selected: selectedOf(ctx),
    readout: ctx.edit?.readout ?? null, note: noteOf(ctx),
  };
  return state === undefined ? { ...base, facingDeg: 0, frame: 0, steps: 'free' } : { ...base, facingDeg: state.facingDeg, frame: state.frame, steps: state.steps };
}

/** `tick()` once per rendered frame; `onHud` is called at most every 100 ms (nothing without a listener). */
export function viewportHud(ctx: ViewportCtx, onHud?: (hud: ViewportHud) => void): { tick(): void } {
  let last = -Infinity;
  return {
    tick: () => {
      const now = performance.now();
      if (onHud === undefined || now - last < PERIOD_MS) return;
      last = now;
      onHud(snapshot(ctx));
    },
  };
}
